/**
 * AJAX Search - No Page Refresh Search System
 * Handles search form submissions, search icon clicks, and pagination without page reload
 */

(function() {
  'use strict';

  const AjaxSearch = {
    selectors: {
      resultsContainer: '[data-ajax-search-results]',
      searchForm: '[data-ajax-search-form]',
      searchInput: '[data-ajax-search-input]',
      searchIcons: '.icon-tag-filters__item',
      pagination: '[data-ajax-pagination]',
      paginationLink: '[data-ajax-pagination-link]',
      anchor: '.ajax-search-anchor'
    },

    state: {
      isLoading: false,
      currentQuery: '',
      currentPage: 1
    },

    init: function() {
      this.resultsContainer = document.querySelector(this.selectors.resultsContainer);
      if (!this.resultsContainer) return;

      this.bindEvents();
      this.highlightActiveIcon();
      this.updateUrlState();
    },

    bindEvents: function() {
      // Search form submissions
      document.querySelectorAll(this.selectors.searchForm).forEach(form => {
        form.addEventListener('submit', this.handleFormSubmit.bind(this));
      });

      // Search icon clicks
      document.querySelectorAll(this.selectors.searchIcons).forEach(icon => {
        icon.addEventListener('click', this.handleIconClick.bind(this));
      });

      // Pagination clicks (delegated)
      document.addEventListener('click', this.handlePaginationClick.bind(this));

      // Browser back/forward
      window.addEventListener('popstate', this.handlePopState.bind(this));
    },

    handleFormSubmit: function(e) {
      e.preventDefault();
      const form = e.target;
      const input = form.querySelector('input[name="q"]');
      if (!input) return;

      const query = input.value.trim();
      if (query) {
        this.performSearch(query, 1);
      }
    },

    handleIconClick: function(e) {
      e.preventDefault();
      const icon = e.currentTarget;
      const href = icon.getAttribute('href');
      if (!href) return;

      const url = new URL(href, window.location.origin);
      const query = url.searchParams.get('q');
      if (query) {
        this.performSearch(query, 1);
      }
    },

    handlePaginationClick: function(e) {
      const link = e.target.closest(this.selectors.paginationLink);
      if (!link) return;

      e.preventDefault();
      const page = link.dataset.page;
      const query = link.dataset.query;
      if (page && query) {
        this.performSearch(query, parseInt(page, 10));
      }
    },

    handlePopState: function(e) {
      const url = new URL(window.location.href);
      const query = url.searchParams.get('q') || '';
      const page = parseInt(url.searchParams.get('page'), 10) || 1;

      if (query) {
        this.performSearch(query, page, false);
      }
    },

    performSearch: function(query, page, pushState = true) {
      if (this.state.isLoading) return;

      this.state.isLoading = true;
      this.state.currentQuery = query;
      this.state.currentPage = page;

      this.showLoading();
      this.highlightActiveIcon(query);

      // Update all search inputs
      document.querySelectorAll(this.selectors.searchInput).forEach(input => {
        input.value = query;
      });

      // Build URL
      const searchUrl = new URL(window.location.pathname, window.location.origin);
      searchUrl.searchParams.set('q', query);
      if (page > 1) {
        searchUrl.searchParams.set('page', page);
      }
      searchUrl.searchParams.set('section_id', this.resultsContainer.dataset.sectionId);

      // Fetch results
      fetch(searchUrl.toString())
        .then(response => response.text())
        .then(html => {
          this.updateResults(html);

          if (pushState) {
            const stateUrl = new URL(window.location.pathname, window.location.origin);
            stateUrl.searchParams.set('q', query);
            if (page > 1) {
              stateUrl.searchParams.set('page', page);
            }
            history.pushState({ query, page }, '', stateUrl.toString());
          }

          this.scrollToResults();
        })
        .catch(error => {
          console.error('Search error:', error);
        })
        .finally(() => {
          this.state.isLoading = false;
          this.hideLoading();
        });
    },

    updateResults: function(html) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newResults = doc.querySelector(this.selectors.resultsContainer);

      if (newResults) {
        this.resultsContainer.innerHTML = newResults.innerHTML;
      }
    },

    showLoading: function() {
      this.resultsContainer.classList.add('is-loading');
    },

    hideLoading: function() {
      this.resultsContainer.classList.remove('is-loading');
    },

    scrollToResults: function() {
      const anchor = document.querySelector(this.selectors.anchor);
      if (anchor) {
        anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    },

    highlightActiveIcon: function(query) {
      const currentQuery = query || new URL(window.location.href).searchParams.get('q') || '';
      const normalizedQuery = currentQuery.toLowerCase().replace(/\s+/g, '');

      document.querySelectorAll(this.selectors.searchIcons).forEach(icon => {
        const href = icon.getAttribute('href');
        if (!href) return;

        const url = new URL(href, window.location.origin);
        const iconQuery = (url.searchParams.get('q') || '').toLowerCase().replace(/\s+/g, '');

        if (iconQuery && normalizedQuery && iconQuery === normalizedQuery) {
          icon.classList.add('is-active');
        } else {
          icon.classList.remove('is-active');
        }
      });
    },

    updateUrlState: function() {
      const url = new URL(window.location.href);
      this.state.currentQuery = url.searchParams.get('q') || '';
      this.state.currentPage = parseInt(url.searchParams.get('page'), 10) || 1;
    }
  };

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => AjaxSearch.init());
  } else {
    AjaxSearch.init();
  }

  // Re-initialize on Shopify section reload
  document.addEventListener('shopify:section:load', () => AjaxSearch.init());
})();
